import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './BarcodePayment.css';
import fetchData from '../../functions/fetchData';
import CardComponent from '../../components/cards/CardComponent';
import FloatingNavModal from '../../components/floatingbuttons/FloatingNavModal';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import axios from 'axios';
import { getHeaders } from '../../utility/getHeader';
import { 
  FaBarcode, 
  FaDollarSign, 
  FaCreditCard, 
  FaUniversity, 
  FaHome,
  FaCheck,
  FaSpinner,
  FaFilePdf,
  FaFileExcel,
  FaPrint,
  FaSort,
  FaSortUp,
  FaSortDown
} from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';

const BarcodePayment = () => {
  const navigate = useNavigate();
  const barcodeInputRef = useRef(null);
  
  // Add error state to track any component-level errors
  const [componentError, setComponentError] = useState(null);
  
  const [formData, setFormData] = useState({
    barcode: '',
    paymentMode: 'cash',
    paidAmount: '',
    balanceAmount: '',
    discount: 0,
    orderDetails: null
  });
  
  const [loading, setLoading] = useState(false);
  const [orderFound, setOrderFound] = useState(false);
  const [pendingRecords, setPendingRecords] = useState([]);
  const [loadingPending, setLoadingPending] = useState(false);
  const [viewMode, setViewMode] = useState('billwise'); // 'payment', 'billwise', 'tablewise'
  const [tableWiseData, setTableWiseData] = useState([]);
  const [loadingTableWise, setLoadingTableWise] = useState(false);
  const [showTableModal, setShowTableModal] = useState(false);
  const [selectedTableData, setSelectedTableData] = useState(null);
  const [billwiseSortConfig, setBillwiseSortConfig] = useState({ key: null, direction: 'asc' });
  const [tablewiseSortConfig, setTablewiseSortConfig] = useState({ key: null, direction: 'asc' });

  // Component error handler
  const handleComponentError = (error, context = '') => {
    console.error(`BarcodePayment ${context} error:`, error);
    setComponentError(error);
    toast.error(`An error occurred: ${error.message}`);
  };

  // Debug current state
  console.log('BarcodePayment Component State:', {
    viewMode,
    pendingRecordsCount: pendingRecords.length,
    tableWiseDataCount: tableWiseData.length,
    loadingPending,
    loadingTableWise,
    pendingRecordsType: typeof pendingRecords,
    tableWiseDataType: typeof tableWiseData
  });

  // Get user information for authentication
  const userType = (localStorage.getItem('usertype') || sessionStorage.getItem('usertype') || '').toLowerCase();
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  
  const isAuthenticated = !!token;
  const isCashier = userType === 'cashier' || userType === 'admin';

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    if (!isCashier) {
      toast.error('Access denied. Only cashiers can access this page.');
      navigate('/dashboard/admin');
      return;
    }

    // Focus on barcode input when component mounts
    if (barcodeInputRef.current) {
      barcodeInputRef.current.focus();
    }

    // Prevent Bootstrap JavaScript from interfering with React
    const preventBootstrapConflicts = () => {
      // Disable any Bootstrap dropdowns or modals that might conflict
      if (window.$ && window.$.fn && window.$.fn.dropdown) {
        window.$(document).off('click.bs.dropdown.data-api');
      }
      if (window.$ && window.$.fn && window.$.fn.modal) {
        window.$(document).off('click.bs.modal.data-api');
      }
    };

    preventBootstrapConflicts();
  }, [isAuthenticated, isCashier, navigate]);

  // Handle barcode input changes
  const handleBarcodeChange = (e) => {
    try {
      const value = e.target.value;
      setFormData(prev => ({ ...prev, barcode: value }));
      
      // Auto-search when barcode is complete (reduced to 8 digits for faster response)
      if (value.length >= 8) {
        handleBarcodeSearch(value);
      }
    } catch (error) {
      handleComponentError(error, 'barcode change');
    }
  };

  // Handle barcode scanning/search and direct API update
  const handleBarcodeSearch = async (barcodeValue = formData.barcode) => {
    if (!barcodeValue.trim()) {
      toast.error('Please enter or scan a barcode');
      return;
    }

    setLoading(true);
    
    // Add error boundary protection
    try {
    try {
      // Search for order by barbill in collection table
      const collectionData = await fetchData('collection', null, 'id', { barbill: barcodeValue });
      
      if (collectionData && collectionData.length > 0) {
        const orderData = collectionData[0];
        
        // Allow rescanning of already paid bills
        let balanceAmount;
        let totalAmount;
        
        if (orderData.balance <= 0) {
          // Bill is already paid - allow rescanning with original amounts
          toast.info('This order is already paid. Allowing rescan...');
          totalAmount = orderData.netamount || (orderData.baramount + orderData.foodamount - orderData.discount);
          balanceAmount = totalAmount; // Reset to full amount for rescanning
        } else {
          // Bill has pending balance
          totalAmount = orderData.netamount || (orderData.baramount + orderData.foodamount - orderData.discount);
          balanceAmount = orderData.balance || totalAmount;
        }
        
        // Set form data temporarily for display
        setFormData(prev => ({ 
          ...prev, 
          orderDetails: orderData,
          paidAmount: balanceAmount.toString(),
          balanceAmount: 0,
          discount: orderData.discount || 0
        }));
        
        setOrderFound(true);
        toast.success(`Order found! Processing payment of ${balanceAmount}...`);
        
        // Auto-process payment with full balance amount - wrapped in try-catch for safety
        try {
          await processPaymentDirectly(orderData, balanceAmount);
        } catch (paymentError) {
          console.error('Payment processing failed:', paymentError);
          toast.error(`Payment processing failed: ${paymentError.message}`);
          // Keep the order data visible for manual processing
        }
        
      } else {
        throw new Error('Order not found');
      }
      
    } catch (searchError) {
      console.error('Error searching order:', searchError);
      toast.error('Order not found. Please check the barcode and try again.');
      setOrderFound(false);
      setFormData(prev => ({ 
        ...prev, 
        orderDetails: null, 
        paidAmount: '',
        balanceAmount: '',
        discount: 0
      }));
    }
    } catch (outerError) {
      console.error('Unexpected error in barcode search:', outerError);
      toast.error('An unexpected error occurred. Please try again.');
      setOrderFound(false);
    } finally {
      setLoading(false);
    }
  };

  // Direct payment processing function
  const processPaymentDirectly = async (orderData, paidAmount) => {
    try {
      // Get user name for the update
      const uname = localStorage.getItem('uname') || sessionStorage.getItem('uname');
      
      // Calculate balance amount (should be 0 for full payment)
      const totalAmount = orderData.netamount || orderData.balance;
      const balanceAmount = Math.max(0, totalAmount - paidAmount);
      
      // Prepare data for the combined API
      const requestData = {
        finalbill_data: {
          id: orderData.id,
          updates: {
            userid: uname,
            status: '0',
            discount: orderData.discount || 0,
            mode: formData.paymentMode,
            paid_status: '1'
          }
        },
        collection_data: {
          id: orderData.id,
          updates: {
            userid: uname,
            paidamount: (formData.paymentMode === 'btransfer' || formData.paymentMode === 'card') ? 0 : paidAmount,
            btransfer: formData.paymentMode === 'btransfer' ? paidAmount : 0,
            card: formData.paymentMode === 'card' ? paidAmount : 0,
            discount: orderData.discount || 0,
            balance: balanceAmount,
            mode: formData.paymentMode,
            flag: '0'
          }
        }
      };

      // Call the combined API
      const response = await axios.put('/cashier/update-finalbill-collection', requestData, getHeaders());

      const result = response.data;

      if (!result.success) {
        throw new Error(result.message || 'Failed to process payment');
      }
      
      toast.success(`Payment of ${paidAmount} processed successfully via ${formData.paymentMode.toUpperCase()}!`);
      console.log('✅ Auto-payment processed successfully:', result);
      
      // Reset form after successful payment but preserve payment mode
      const currentPaymentMode = formData.paymentMode;
      setFormData({
        barcode: '',
        paymentMode: currentPaymentMode, // Preserve current payment mode
        paidAmount: '',
        balanceAmount: '',
        discount: 0,
        orderDetails: null
      });
      setOrderFound(false);
      
      // Refresh pending records and table wise data
      fetchPendingRecords();
      fetchTableWiseData();
      
      // Focus back on barcode input for next transaction
      if (barcodeInputRef.current) {
        barcodeInputRef.current.focus();
      }
      
    } catch (error) {
      console.error('❌ Auto-payment processing error:', error);
      toast.error(`Auto-payment failed: ${error.message}`);
      
      // Keep the order data visible for manual processing if auto-payment fails
      setFormData(prev => ({
        ...prev,
        paidAmount: paidAmount.toString(),
        balanceAmount: 0
      }));
    }
  };

  // Handle payment mode change
  const handlePaymentModeChange = (e) => {
    setFormData(prev => ({ ...prev, paymentMode: e.target.value }));
  };

  // Direct payment processing function
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleBarcodeSearch();
    }
  };

  const getPaymentIcon = () => {
    switch (formData.paymentMode) {
      case 'cash': return <FaDollarSign className="text-success" />;
      case 'card': return <FaCreditCard className="text-primary" />;
      case 'btransfer': return <FaUniversity className="text-info" />;
      default: return <FaDollarSign className="text-success" />;
    }
  };

  // Sort billwise table
  const handleBillwiseSort = (key) => {
    let direction = 'asc';
    if (billwiseSortConfig.key === key && billwiseSortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setBillwiseSortConfig({ key, direction });
  };

  // Sort tablewise table
  const handleTablewiseSort = (key) => {
    let direction = 'asc';
    if (tablewiseSortConfig.key === key && tablewiseSortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setTablewiseSortConfig({ key, direction });
  };

  // Get sort icon
  const getSortIcon = (key, sortConfig) => {
    if (sortConfig.key !== key) {
      return <FaSort className="ms-1" style={{ opacity: 0.3 }} />;
    }
    return sortConfig.direction === 'asc' ? <FaSortUp className="ms-1" /> : <FaSortDown className="ms-1" />;
  };

  // Sort pending records
  const sortedPendingRecords = React.useMemo(() => {
    if (!billwiseSortConfig.key) return pendingRecords;
    
    const sorted = [...pendingRecords].sort((a, b) => {
      const aVal = a[billwiseSortConfig.key];
      const bVal = b[billwiseSortConfig.key];
      
      if (aVal < bVal) return billwiseSortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return billwiseSortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    
    return sorted;
  }, [pendingRecords, billwiseSortConfig]);

  // Sort table wise data
  const sortedTableWiseData = React.useMemo(() => {
    if (!tablewiseSortConfig.key) return tableWiseData;
    
    const sorted = [...tableWiseData].sort((a, b) => {
      const aVal = a[tablewiseSortConfig.key];
      const bVal = b[tablewiseSortConfig.key];
      
      if (aVal < bVal) return tablewiseSortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return tablewiseSortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    
    return sorted;
  }, [tableWiseData, tablewiseSortConfig]);

  // Fetch pending records where balance > 0
  const fetchPendingRecords = async () => {
    setLoadingPending(true);
    console.log('Fetching pending records...');
    try {
      const { fetchdatanotequal } = await import('../../functions/fetchData');
      console.log('Calling fetchdatanotequal with params:', {
        table: 'collection',
        orderBy: 'id',
        where: { balance: 0 }
      });
      
      const pendingData = await fetchdatanotequal('collection', null, 'id', { balance: 0 });
      console.log('Received pending data:', pendingData);
      console.log('Pending records count:', pendingData?.length || 0);
      console.log('Sample pending record:', pendingData?.[0]);
      
      if (pendingData && pendingData.length > 0) {
        setPendingRecords(pendingData);
        console.log('Set pending records state with', pendingData.length, 'records');
      } else {
        setPendingRecords([]);
        console.log('No pending data found, set empty array');
      }
    } catch (error) {
      console.error('Error fetching pending records:', error);
      toast.error('Failed to load pending records');
    } finally {
      setLoadingPending(false);
    }
  };

  // Load initial data only when needed
  useEffect(() => {
    // Load billwise pending data by default when component mounts
    if (isAuthenticated && isCashier) {
      fetchPendingRecords();
    }
  }, [isAuthenticated, isCashier]);

  // Fetch table wise data grouped by table number
  const fetchTableWiseData = async () => {
    setLoadingTableWise(true);
    console.log('Fetching table wise data...');
    try {
      const { fetchdatanotequal } = await import('../../functions/fetchData');
      console.log('Calling fetchdatanotequal for table wise data with params:', {
        table: 'collection',
        orderBy: 'tables',
        where: { balance: 0 }
      });
      
      const pendingData = await fetchdatanotequal('collection', null, 'tables', { balance: 0 });
      console.log('Received table wise raw data:', pendingData);
      console.log('Raw data count:', pendingData?.length || 0);
      
      if (!pendingData || pendingData.length === 0) {
        console.log('No pending data found for table grouping');
        setTableWiseData([]);
        return;
      }
      
      // Group by table number and calculate totals
      const groupedData = {};
      pendingData.forEach(record => {
        const tableNo = record.tables || 'Unknown';
        console.log('Processing record for table:', tableNo, 'Record:', record);
        
        if (!groupedData[tableNo]) {
          groupedData[tableNo] = {
            tableNumber: tableNo,
            records: [],
            totalNetAmount: 0,
            totalPaidAmount: 0,
            totalBalance: 0,
            recordCount: 0
          };
        }
        groupedData[tableNo].records.push(record);
        groupedData[tableNo].totalNetAmount += parseFloat(record.netamount) || 0;
        groupedData[tableNo].totalPaidAmount += parseFloat(record.paidamount) || 0;
        groupedData[tableNo].totalBalance += parseFloat(record.balance) || 0;
        groupedData[tableNo].recordCount += 1;
      });
      
      console.log('Grouped data by table:', groupedData);
      
      // Convert to array, filter out tables with zero balance, and sort by table number
      const tableWiseArray = Object.values(groupedData)
        .filter(table => table.totalBalance > 0) // Only show tables with pending balances
        .sort((a, b) => {
          const aNum = parseInt(a.tableNumber);
          const bNum = parseInt(b.tableNumber);
          if (isNaN(aNum) && isNaN(bNum)) return a.tableNumber.localeCompare(b.tableNumber);
          if (isNaN(aNum)) return 1;
          if (isNaN(bNum)) return -1;
          return aNum - bNum;
        });
      
      console.log('Final table wise array (filtered for pending balances):', tableWiseArray);
      console.log('Setting tableWiseData state with', tableWiseArray.length, 'tables with pending amounts');
      setTableWiseData(tableWiseArray);
    } catch (error) {
      console.error('Error fetching table wise data:', error);
      toast.error('Failed to load table wise data');
    } finally {
      setLoadingTableWise(false);
    }
  };

  // Export functions
  const exportToPDF = () => {
    if (!selectedTableData) return;
    
    try {
      const doc = new jsPDF();
      
      // Add title
      doc.setFontSize(18);
      doc.setFont(undefined, 'bold');
      doc.text(`Table ${selectedTableData.tableNumber} - Pending Bills Details`, doc.internal.pageSize.getWidth() / 2, 20, { align: 'center' });
      
      // Add summary boxes
      doc.setFontSize(12);
      doc.setFont(undefined, 'normal');
      
      const startY = 40;
      const boxWidth = 40;
      const boxHeight = 25;
      const spacing = 45;
      
      // Summary data
      const summaryItems = [
        { label: 'Total Orders', value: selectedTableData.recordCount },
        { label: 'Net Amount', value: `${selectedTableData.totalNetAmount.toLocaleString()}` },
        { label: 'Paid Amount', value: `${selectedTableData.totalPaidAmount.toLocaleString()}` },
        { label: 'Balance Due', value: `${selectedTableData.totalBalance.toLocaleString()}` }
      ];
      
      summaryItems.forEach((item, index) => {
        const x = 15 + (index * spacing);
        
        // Draw box
        doc.rect(x, startY, boxWidth, boxHeight);
        
        // Add value (bold, centered)
        doc.setFont(undefined, 'bold');
        doc.setFontSize(14);
        doc.text(String(item.value), x + boxWidth/2, startY + 12, { align: 'center' });
        
        // Add label (normal, centered)
        doc.setFont(undefined, 'normal');
        doc.setFontSize(9);
        doc.text(item.label, x + boxWidth/2, startY + 20, { align: 'center' });
      });
      
      // Prepare table data
      const tableData = selectedTableData.records.map(record => [
        record.barbill,
        record.dte,
        record.tme ? record.tme.split('.')[0] : '',
        `${parseFloat(record.netamount).toLocaleString()}`,
        `${parseFloat(record.paidamount || 0).toLocaleString()}`,
        `${parseFloat(record.balance).toLocaleString()}`,
        record.mode || 'PENDING'
      ]);
      
      // Add table
      doc.autoTable({
        head: [['Bill No.', 'Date', 'Time', 'Net Amount', 'Paid Amount', 'Balance', 'Payment Mode']],
        body: tableData,
        startY: startY + boxHeight + 15,
        styles: {
          fontSize: 10,
          cellPadding: 4,
        },
        headStyles: {
          fillColor: [242, 242, 242],
          textColor: [0, 0, 0],
          fontStyle: 'bold'
        },
        columnStyles: {
          3: { halign: 'right' },
          4: { halign: 'right' },
          5: { halign: 'right' }
        }
      });
      
      // Add footer
      const finalY = doc.lastAutoTable.finalY + 15;
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      doc.text(`Generated on: ${new Date().toLocaleString()}`, doc.internal.pageSize.getWidth() / 2, finalY, { align: 'center' });
      
      // Save the PDF
      const fileName = `Table_${selectedTableData.tableNumber}_Pending_Bills_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
      toast.success('PDF downloaded successfully');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF');
    }
  };

  const exportToExcel = () => {
    if (!selectedTableData) return;
    
    let csvContent = "data:text/csv;charset=utf-8,";
    csvContent += `Table ${selectedTableData.tableNumber} - Pending Bills Details\n\n`;
    csvContent += `Summary:\n`;
    csvContent += `Total Orders,${selectedTableData.recordCount}\n`;
    csvContent += `Net Amount,${selectedTableData.totalNetAmount.toLocaleString()}\n`;
    csvContent += `Paid Amount,${selectedTableData.totalPaidAmount.toLocaleString()}\n`;
    csvContent += `Balance Due,${selectedTableData.totalBalance.toLocaleString()}\n\n`;
    
    csvContent += "Bill No.,Date,Time,Net Amount,Paid Amount,Balance,Payment Mode\n";
    
    selectedTableData.records.forEach(record => {
      csvContent += `${record.barbill},${record.dte},${record.tme ? record.tme.split('.')[0] : ''},${parseFloat(record.netamount).toLocaleString()},${parseFloat(record.paidamount || 0).toLocaleString()},${parseFloat(record.balance).toLocaleString()},${record.mode || 'PENDING'}\n`;
    });
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `Table_${selectedTableData.tableNumber}_Pending_Bills_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Excel file downloaded');
  };

  const printThermal = () => {
    if (!selectedTableData) return;
    
    const thermalContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Thermal Print - Table ${selectedTableData.tableNumber}</title>
        <style>
          @media print {
            @page { margin: 0; size: 80mm auto; }
            body { margin: 0; }
          }
          body {
            font-family: monospace;
            font-size: 12px;
            line-height: 1.2;
            margin: 0;
            padding: 5mm;
            width: 70mm;
          }
          .center { text-align: center; }
          .bold { font-weight: bold; }
          .divider { border-bottom: 1px dashed #000; margin: 5px 0; }
          .row { display: flex; justify-content: space-between; }
          .small { font-size: 10px; }
        </style>
      </head>
      <body>
        <div class="center bold">JANNAAT CLUB</div>
        <div class="center small">Table ${selectedTableData.tableNumber} - Pending Bills</div>
        <div class="divider"></div>
        
        <div class="row bold">
          <span>Total Orders:</span>
          <span>${selectedTableData.recordCount}</span>
        </div>
        <div class="row">
          <span>Net Amount:</span>
          <span>฿${selectedTableData.totalNetAmount.toLocaleString()}</span>
        </div>
        <div class="row">
          <span>Paid Amount:</span>
          <span>฿${selectedTableData.totalPaidAmount.toLocaleString()}</span>
        </div>
        <div class="row bold">
          <span>Balance Due:</span>
          <span>฿${selectedTableData.totalBalance.toLocaleString()}</span>
        </div>
        
        <div class="divider"></div>
        <div class="center bold">BILL DETAILS</div>
        <div class="divider"></div>
        
        ${selectedTableData.records.map(record => `
          <div style="margin-bottom: 8px;">
            <div class="bold">Bill: ${record.barbill}</div>
            <div class="small">${record.dte} ${record.tme ? record.tme.split('.')[0] : ''}</div>
            <div class="row">
              <span>Amount:</span>
              <span>฿${parseFloat(record.netamount).toLocaleString()}</span>
            </div>
            <div class="row">
              <span>Paid:</span>
              <span>฿${parseFloat(record.paidamount || 0).toLocaleString()}</span>
            </div>
            <div class="row bold">
              <span>Balance:</span>
              <span>฿${parseFloat(record.balance).toLocaleString()}</span>
            </div>
            <div class="row small">
              <span>Mode:</span>
              <span>${record.mode || 'PENDING'}</span>
            </div>
            <div style="border-bottom: 1px dotted #ccc; margin: 3px 0;"></div>
          </div>
        `).join('')}
        
        <div class="center small" style="margin-top: 10px;">
          ${new Date().toLocaleString()}
        </div>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank', 'width=800,height=800,scrollbars=yes,resizable=yes');
    printWindow.document.write(thermalContent);
    printWindow.document.close();
    printWindow.print();
    toast.success('Thermal print initiated');
  };

  // Export functions for Bill Wise Pending
  const exportBillwiseToPDF = () => {
    if (pendingRecords.length === 0) return;
    
    const totalPending = pendingRecords.reduce((total, record) => total + (parseFloat(record.balance) || 0), 0);
    
    try {
      const doc = new jsPDF();
      
      // Add title
      doc.setFontSize(20);
      doc.setFont(undefined, 'bold');
      doc.text('Bill Wise Pending Report', doc.internal.pageSize.getWidth() / 2, 20, { align: 'center' });
      
      // Add summary info
      doc.setFontSize(12);
      doc.setFont(undefined, 'normal');
      doc.text(`Total Records: ${pendingRecords.length}`, 20, 40);
      doc.text(`Total Pending Amount: ${totalPending.toLocaleString()}`, 20, 50);
      doc.text(`Generated on: ${new Date().toLocaleString()}`, 20, 60);
      
      // Prepare table data
      const tableData = pendingRecords.map(record => [
        record.barbill,
        `Table ${record.tables}`,
        record.dte,
        record.tme ? record.tme.split('.')[0] : '',
        `${parseFloat(record.netamount).toLocaleString()}`,
        `${parseFloat(record.paidamount || 0).toLocaleString()}`,
        `${parseFloat(record.balance).toLocaleString()}`,
        record.mode || 'PENDING'
      ]);
      
      // Add total row
      tableData.push([
        { content: 'Total Pending Amount:', colSpan: 6, styles: { halign: 'right', fontStyle: 'bold' } },
        { content: `${totalPending.toLocaleString()}`, styles: { fontStyle: 'bold' } },
        ''
      ]);
      
      // Add table
      doc.autoTable({
        head: [['Bill No.', 'Table', 'Date', 'Time', 'Net Amount', 'Paid Amount', 'Balance', 'Payment Mode']],
        body: tableData,
        startY: 75,
        styles: {
          fontSize: 9,
          cellPadding: 3,
        },
        headStyles: {
          fillColor: [255, 193, 7], // Bootstrap warning color
          textColor: [0, 0, 0],
          fontStyle: 'bold'
        },
        columnStyles: {
          4: { halign: 'right' },
          5: { halign: 'right' },
          6: { halign: 'right' }
        }
      });
      
      // Save the PDF
      const fileName = `Billwise_Pending_Report_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
      toast.success('PDF downloaded successfully');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF');
    }
  };

  const exportBillwiseToExcel = () => {
    if (pendingRecords.length === 0) return;
    
    const totalPending = pendingRecords.reduce((total, record) => total + (parseFloat(record.balance) || 0), 0);
    
    let csvContent = "data:text/csv;charset=utf-8,";
    csvContent += "Bill Wise Pending Report\\n\\n";
    csvContent += `Total Records,${pendingRecords.length}\\n`;
    csvContent += `Total Pending Amount,${totalPending.toLocaleString()}\\n`;
    csvContent += `Generated on,${new Date().toLocaleString()}\\n\\n`;
    
    csvContent += "Bill No.,Table,Date,Time,Net Amount,Paid Amount,Balance,Payment Mode\\n";
    
    pendingRecords.forEach(record => {
      csvContent += `${record.barbill},Table ${record.tables},${record.dte},${record.tme ? record.tme.split('.')[0] : ''},${parseFloat(record.netamount).toLocaleString()},${parseFloat(record.paidamount || 0).toLocaleString()},${parseFloat(record.balance).toLocaleString()},${record.mode || 'PENDING'}\\n`;
    });
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `Billwise_Pending_Report_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Bill wise Excel file downloaded');
  };

  const printBillwiseThermal = () => {
    if (pendingRecords.length === 0) return;
    
    const totalPending = pendingRecords.reduce((total, record) => total + (parseFloat(record.balance) || 0), 0);
    
    const thermalContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Bill Wise Pending - Thermal Print</title>
        <style>
          @media print {
            @page { margin: 0; size: 80mm auto; }
            body { margin: 0; }
          }
          body {
            font-family: monospace;
            font-size: 11px;
            line-height: 1.2;
            margin: 0;
            padding: 5mm;
            width: 70mm;
          }
          .center { text-align: center; }
          .bold { font-weight: bold; }
          .divider { border-bottom: 1px dashed #000; margin: 5px 0; }
          .row { display: flex; justify-content: space-between; }
          .small { font-size: 9px; }
        </style>
      </head>
      <body>
        <div class="center bold">JANNAAT CLUB</div>
        <div class="center small">Bill Wise Pending Report</div>
        <div class="divider"></div>
        
        <div class="row bold">
          <span>Total Records:</span>
          <span>${pendingRecords.length}</span>
        </div>
        <div class="row bold">
          <span>Total Pending:</span>
          <span>฿${totalPending.toLocaleString()}</span>
        </div>
        
        <div class="divider"></div>
        <div class="center bold">PENDING BILLS</div>
        <div class="divider"></div>
        
        ${pendingRecords.map(record => `
          <div style="margin-bottom: 8px;">
            <div class="bold">Bill: ${record.barbill}</div>
            <div class="small">Table ${record.tables} | ${record.dte}</div>
            <div class="row">
              <span>Net:</span>
              <span>฿${parseFloat(record.netamount).toLocaleString()}</span>
            </div>
            <div class="row">
              <span>Paid:</span>
              <span>฿${parseFloat(record.paidamount || 0).toLocaleString()}</span>
            </div>
            <div class="row bold">
              <span>Balance:</span>
              <span>฿${parseFloat(record.balance).toLocaleString()}</span>
            </div>
            <div class="row small">
              <span>Mode:</span>
              <span>${record.mode || 'PENDING'}</span>
            </div>
            <div style="border-bottom: 1px dotted #ccc; margin: 3px 0;"></div>
          </div>
        `).join('')}
        
        <div class="divider"></div>
        <div class="row bold">
          <span>TOTAL PENDING:</span>
          <span>฿${totalPending.toLocaleString()}</span>
        </div>
        
        <div class="center small" style="margin-top: 10px;">
          ${new Date().toLocaleString()}
        </div>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank', 'width=800,height=800,scrollbars=yes,resizable=yes');
    printWindow.document.write(thermalContent);
    printWindow.document.close();
    printWindow.print();
    toast.success('Bill wise thermal print initiated');
  };

  // Export functions for Table Wise Pending
  const exportTablewiseToPDF = () => {
    if (tableWiseData.length === 0) return;
    
    const totalOrders = tableWiseData.reduce((total, table) => total + table.recordCount, 0);
    const totalNetAmount = tableWiseData.reduce((total, table) => total + table.totalNetAmount, 0);
    const totalPaidAmount = tableWiseData.reduce((total, table) => total + table.totalPaidAmount, 0);
    const totalBalance = tableWiseData.reduce((total, table) => total + table.totalBalance, 0);
    
    try {
      const doc = new jsPDF();
      
      // Add title
      doc.setFontSize(20);
      doc.setFont(undefined, 'bold');
      doc.text('Table Wise Pending Report', doc.internal.pageSize.getWidth() / 2, 20, { align: 'center' });
      
      // Add summary info
      doc.setFontSize(12);
      doc.setFont(undefined, 'normal');
      doc.text(`Total Tables: ${tableWiseData.length}`, 20, 40);
      doc.text(`Total Orders: ${totalOrders}`, 20, 50);
      doc.text(`Total Balance Amount: ${totalBalance.toLocaleString()}`, 20, 60);
      doc.text(`Generated on: ${new Date().toLocaleString()}`, 20, 70);
      
      // Prepare table data
      const tableData = tableWiseData.map(table => [
        `Table ${table.tableNumber}`,
        `${table.recordCount} orders`,
        `${table.totalNetAmount.toLocaleString()}`,
        `${table.totalPaidAmount.toLocaleString()}`,
        `${table.totalBalance.toLocaleString()}`
      ]);
      
      // Add total row
      tableData.push([
        { content: 'Total:', styles: { fontStyle: 'bold' } },
        { content: `${totalOrders} orders`, styles: { fontStyle: 'bold' } },
        { content: `${totalNetAmount.toLocaleString()}`, styles: { fontStyle: 'bold' } },
        { content: `${totalPaidAmount.toLocaleString()}`, styles: { fontStyle: 'bold' } },
        { content: `${totalBalance.toLocaleString()}`, styles: { fontStyle: 'bold' } }
      ]);
      
      // Add table
      doc.autoTable({
        head: [['Table No.', 'Orders Count', 'Total Net Amount', 'Total Paid Amount', 'Total Balance']],
        body: tableData,
        startY: 85,
        styles: {
          fontSize: 10,
          cellPadding: 4,
        },
        headStyles: {
          fillColor: [23, 162, 184], // Bootstrap info color
          textColor: [255, 255, 255],
          fontStyle: 'bold'
        },
        columnStyles: {
          2: { halign: 'right' },
          3: { halign: 'right' },
          4: { halign: 'right' }
        }
      });
      
      // Save the PDF
      const fileName = `Tablewise_Pending_Report_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
      toast.success('PDF downloaded successfully');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF');
    }
  };

  const exportTablewiseToExcel = () => {
    if (tableWiseData.length === 0) return;
    
    const totalOrders = tableWiseData.reduce((total, table) => total + table.recordCount, 0);
    const totalNetAmount = tableWiseData.reduce((total, table) => total + table.totalNetAmount, 0);
    const totalPaidAmount = tableWiseData.reduce((total, table) => total + table.totalPaidAmount, 0);
    const totalBalance = tableWiseData.reduce((total, table) => total + table.totalBalance, 0);
    
    let csvContent = "data:text/csv;charset=utf-8,";
    csvContent += "Table Wise Pending Report\\n\\n";
    csvContent += `Total Tables,${tableWiseData.length}\\n`;
    csvContent += `Total Orders,${totalOrders}\\n`;
    csvContent += `Total Net Amount,${totalNetAmount.toLocaleString()}\\n`;
    csvContent += `Total Paid Amount,${totalPaidAmount.toLocaleString()}\\n`;
    csvContent += `Total Balance Amount,${totalBalance.toLocaleString()}\\n`;
    csvContent += `Generated on,${new Date().toLocaleString()}\\n\\n`;
    
    csvContent += "Table No.,Orders Count,Total Net Amount,Total Paid Amount,Total Balance\\n";
    
    tableWiseData.forEach(table => {
      csvContent += `Table ${table.tableNumber},${table.recordCount} orders,${table.totalNetAmount.toLocaleString()},${table.totalPaidAmount.toLocaleString()},${table.totalBalance.toLocaleString()}\\n`;
    });
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `Tablewise_Pending_Report_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Table wise Excel file downloaded');
  };

  const printTablewiseThermal = () => {
    if (tableWiseData.length === 0) return;
    
    const totalOrders = tableWiseData.reduce((total, table) => total + table.recordCount, 0);
    const totalBalance = tableWiseData.reduce((total, table) => total + table.totalBalance, 0);
    
    const thermalContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Table Wise Pending - Thermal Print</title>
        <style>
          @media print {
            @page { margin: 0; size: 80mm auto; }
            body { margin: 0; }
          }
          body {
            font-family: monospace;
            font-size: 11px;
            line-height: 1.2;
            margin: 0;
            padding: 5mm;
            width: 70mm;
          }
          .center { text-align: center; }
          .bold { font-weight: bold; }
          .divider { border-bottom: 1px dashed #000; margin: 5px 0; }
          .row { display: flex; justify-content: space-between; }
          .small { font-size: 9px; }
        </style>
      </head>
      <body>
        <div class="center bold">JANNAAT CLUB</div>
        <div class="center small">Table Wise Pending Report</div>
        <div class="divider"></div>
        
        <div class="row bold">
          <span>Total Tables:</span>
          <span>${tableWiseData.length}</span>
        </div>
        <div class="row bold">
          <span>Total Orders:</span>
          <span>${totalOrders}</span>
        </div>
        <div class="row bold">
          <span>Total Balance:</span>
          <span>฿${totalBalance.toLocaleString()}</span>
        </div>
        
        <div class="divider"></div>
        <div class="center bold">TABLE SUMMARY</div>
        <div class="divider"></div>
        
        ${tableWiseData.map(table => `
          <div style="margin-bottom: 8px;">
            <div class="bold">Table ${table.tableNumber}</div>
            <div class="row">
              <span>Orders:</span>
              <span>${table.recordCount}</span>
            </div>
            <div class="row">
              <span>Net Amount:</span>
              <span>฿${table.totalNetAmount.toLocaleString()}</span>
            </div>
            <div class="row">
              <span>Paid:</span>
              <span>฿${table.totalPaidAmount.toLocaleString()}</span>
            </div>
            <div class="row bold">
              <span>Balance:</span>
              <span>฿${table.totalBalance.toLocaleString()}</span>
            </div>
            <div style="border-bottom: 1px dotted #ccc; margin: 3px 0;"></div>
          </div>
        `).join('')}
        
        <div class="divider"></div>
        <div class="row bold">
          <span>TOTAL BALANCE:</span>
          <span>฿${totalBalance.toLocaleString()}</span>
        </div>
        
        <div class="center small" style="margin-top: 10px;">
          ${new Date().toLocaleString()}
        </div>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank', 'width=800,height=800,scrollbars=yes,resizable=yes');
    printWindow.document.write(thermalContent);
    printWindow.document.close();
    printWindow.print();
    toast.success('Table wise thermal print initiated');
  };

  if (!isAuthenticated) {
    return null; // Will redirect in useEffect
  }

  // If there's a component error, show a recovery UI
  if (componentError) {
    return (
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="alert alert-danger">
              <h4>Payment System Error</h4>
              <p>There was an issue with the barcode payment system. Please refresh the page to try again.</p>
              <button 
                className="btn btn-primary me-2"
                onClick={() => window.location.reload()}
              >
                Refresh Page
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => {
                  setComponentError(null);
                  setFormData({
                    barcode: '',
                    paymentMode: 'cash',
                    paidAmount: '',
                    balanceAmount: '',
                    discount: 0,
                    orderDetails: null
                  });
                  setOrderFound(false);
                }}
              >
                Reset Component
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="barcode-payment">
      <style jsx>{`
        .table-modal {
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          padding: 1rem;
        }
        .table-modal .modal-dialog {
          margin: 0 !important;
        }
        @media (max-width: 768px) {
          .table-modal .modal-dialog {
            max-width: 95vw !important;
            width: 95vw !important;
          }
          .table-modal .modal-content {
            max-height: 90vh !important;
          }
          .table-modal .modal-body {
            max-height: calc(90vh - 120px) !important;
            padding: 1rem !important;
          }
          .table-modal {
            padding: 0.5rem;
          }
        }
        @media (max-width: 576px) {
          .table-modal .modal-dialog {
            max-width: 98vw !important;
            width: 98vw !important;
          }
          .table-modal .modal-content {
            max-height: 95vh !important;
          }
          .table-modal .modal-body {
            max-height: calc(95vh - 100px) !important;
            padding: 0.75rem !important;
          }
          .table-modal {
            padding: 0.25rem;
          }
        }
      `}</style>
      <ToastContainer />
      
      <div className="container-fluid">
        {/* Header */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center flex-wrap mb-3">
              <div className="d-flex align-items-center">
               
                <h2 className="mb-0">
                  <FaBarcode /> Barcode Payment Collection
                </h2>
              </div>
            </div>
            <p className="text-muted">Scan barcode to automatically process payment for orders</p>
          </div>
        </div>

        {/* Main Content */}
        <div className="row">
          {/* Left Side - Barcode Scanner & Payment */}
          <div className="col-12 mb-4">
            <CardComponent title="Scan & Pay" headerColor="warning" pull="left">
              <div className="barcode-scanner-section">
                {/* Single Row Layout for All Fields */}
                <div className="row g-3 mb-4">
                  {/* Barcode Input */}
                  <div className="col-md-4">
                    <label className="form-label fw-bold mb-1">
                      <FaBarcode /> Scan Barcode
                    </label>
                    <div className="input-group">
                      <span className="input-group-text">
                        <FaBarcode />
                      </span>
                      <input
                        ref={barcodeInputRef}
                        type="text"
                        className="form-control"
                        placeholder="Scan or enter barcode..."
                        value={formData.barcode}
                        onChange={handleBarcodeChange}
                        onKeyPress={handleKeyPress}
                        disabled={loading}
                      />
                     
                    </div>
                  </div>

                  {/* Payment Mode */}
                  <div className="col-md-3">
                    <label className="form-label fw-bold mb-1">
                      {getPaymentIcon()} Payment Mode
                    </label>
                    <select 
                      name="type" 
                      id="pmode" 
                      className="form-control"
                      value={formData.paymentMode}
                      onChange={handlePaymentModeChange}
                      disabled={loading}
                    >
                      <option value="cash">Cash</option>
                      <option value="btransfer">Bank Transfer</option>
                      <option value="card">Card Swipe</option>
                    </select>
                  </div>

                  {/* Paid Amount - Display Only */}
                  {orderFound && (
                    <div className="col-md-3">
                      <label className="form-label fw-bold mb-1">Processing Payment</label>
                      <div className="input-group">
                        <span className="input-group-text">₹</span>
                        <input
                          type="number"
                          className="form-control"
                          value={formData.paidAmount}
                          placeholder="Auto-processing payment..."
                          disabled={true}
                          readOnly
                        />
                      </div>
                    </div>
                  )}

                  {/* Status Display */}
                  {orderFound && (
                    <div className="col-md-2 d-flex align-items-end">
                      <div className="w-100 text-center">
                        {loading ? (
                          <div className="alert alert-warning mb-0 py-2">
                            <FaSpinner className="fa-spin me-2" />
                            Processing...
                          </div>
                        ) : (
                          <div className="alert alert-success mb-0 py-2">
                            <FaCheck className="me-2" />
                            Payment Processed
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Order Details and Balance Info Row */}
                {formData.orderDetails && (
                  <div className="row mb-3">
                    <div className="col-12">
                      <div className="alert alert-info mb-0 py-2">
                        <div className="row text-center">
                          <div className="col-md-3">
                            <small className="text-muted d-block">Total Amount</small>
                            <strong>₹{formData.orderDetails.netamount || formData.orderDetails.balance}</strong>
                          </div>
                          <div className="col-md-3">
                            <small className="text-muted d-block">Paid Amount</small>
                            <strong className="text-success">₹{formData.paidAmount || 0}</strong>
                          </div>
                          <div className="col-md-3">
                            <small className="text-muted d-block">Balance</small>
                            <strong className="text-warning">₹{formData.balanceAmount}</strong>
                          </div>
                          <div className="col-md-3">
                            <small className="text-muted d-block">Payment Mode</small>
                            <strong>{formData.paymentMode.charAt(0).toUpperCase() + formData.paymentMode.slice(1)}</strong>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Helper Text */}
                {/* <div className="row">
                  <div className="col-12">
                    <small className="text-muted">
                      <FaBarcode className="me-1" />
                      Point scanner at barcode or enter manually to search for orders
                    </small>
                  </div>
                </div> */}
              </div>
            </CardComponent>
          </div>

      
        </div>

        {/* Navigation Buttons */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="d-flex justify-content-center gap-3 flex-wrap">
             
              <button 
                className={`btn ${viewMode === 'billwise' ? 'btn-warning' : 'btn-dark'} btn-lg`}
                onClick={() => {
                  setViewMode('billwise');
                  if (pendingRecords.length === 0) {
                    fetchPendingRecords();
                  }
                }}
              >
                <FaBarcode className="me-2" />
                Bill Wise Pending ({pendingRecords.length})
              </button>
              <button 
                className={`btn ${viewMode === 'tablewise' ? 'btn-info' : 'btn-dark'} btn-lg`}
                onClick={() => {
                  setViewMode('tablewise');
                  if (tableWiseData.length === 0) {
                    fetchTableWiseData();
                  }
                }}
              >
                <FaHome className="me-2" />
                Table Wise Pending ({tableWiseData.length})
              </button>
            </div>
          </div>
        </div>

        {/* Content based on selected view */}
        {viewMode === 'billwise' && (
          <div className="row">
            <div className="col-12">
              <CardComponent 
                title={`Pending Payments (${pendingRecords.length} records with balance > 0)`}
                headerColor="warning" 
                pull="left"
              >
                {/* Export Buttons for Billwise */}
                {pendingRecords.length > 0 && (
                  <div className="d-flex justify-content-end gap-2 mb-3">
                    <button 
                      type="button" 
                      className="btn btn-success btn-sm"
                      onClick={exportBillwiseToPDF}
                    >
                      <FaFilePdf className="me-1" />
                      Export PDF
                    </button>
                    <button 
                      type="button" 
                      className="btn btn-info btn-sm"
                      onClick={exportBillwiseToExcel}
                    >
                      <FaFileExcel className="me-1" />
                      Export Excel
                    </button>
                    <button 
                      type="button" 
                      className="btn btn-warning btn-sm"
                      onClick={printBillwiseThermal}
                    >
                      <FaPrint className="me-1" />
                      Thermal Print
                    </button>
                  </div>
                )}
                {loadingPending ? (
                  <div className="text-center py-3">
                    <FaSpinner className="fa-spin me-2" />
                    Loading pending records...
                  </div>
                ) : pendingRecords.length === 0 ? (
                  <div className="text-center text-muted py-4">
                    <FaCheck size={32} className="mb-3 text-success" />
                    <h6>No Pending Payments</h6>
                    <p>All orders have been fully paid!</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-striped table-hover">
                      <thead className="table-warning">
                        <tr>
                          <th onClick={() => handleBillwiseSort('barbill')} style={{ cursor: 'pointer', userSelect: 'none', color: 'white' }}>
                            Bill No. {getSortIcon('barbill', billwiseSortConfig)}
                          </th>
                          <th onClick={() => handleBillwiseSort('tables')} style={{ cursor: 'pointer', userSelect: 'none', color: 'white' }}>
                            Table {getSortIcon('tables', billwiseSortConfig)}
                          </th>
                          <th onClick={() => handleBillwiseSort('dte')} style={{ cursor: 'pointer', userSelect: 'none', color: 'white' }}>
                            Date {getSortIcon('dte', billwiseSortConfig)}
                          </th>
                          <th onClick={() => handleBillwiseSort('tme')} style={{ cursor: 'pointer', userSelect: 'none', color: 'white' }}>
                            Time {getSortIcon('tme', billwiseSortConfig)}
                          </th>
                          <th onClick={() => handleBillwiseSort('netamount')} style={{ cursor: 'pointer', userSelect: 'none', color: 'white' }}>
                            Net Amount {getSortIcon('netamount', billwiseSortConfig)}
                          </th>
                          <th onClick={() => handleBillwiseSort('paidamount')} style={{ cursor: 'pointer', userSelect: 'none', color: 'white' }}>
                            Paid {getSortIcon('paidamount', billwiseSortConfig)}
                          </th>
                          <th onClick={() => handleBillwiseSort('balance')} style={{ cursor: 'pointer', userSelect: 'none', color: 'white' }}>
                            Balance {getSortIcon('balance', billwiseSortConfig)}
                          </th>
                          <th onClick={() => handleBillwiseSort('mode')} style={{ cursor: 'pointer', userSelect: 'none', color: 'white' }}>
                            Mode {getSortIcon('mode', billwiseSortConfig)}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedPendingRecords.map((record, index) => (
                          <tr key={record.id || index}>
                            <td>
                              <strong className="text-primary">{record.barbill}</strong>
                            </td>
                            <td>{record.tables}</td>
                            <td>{record.dte}</td>
                            <td>{record.tme ? record.tme.split('.')[0] : ''}</td>
                            <td>₹{record.netamount}</td>
                            <td>₹{record.paidamount || 0}</td>
                            <td>
                              <span className="badge bg-warning text-dark">
                                ₹{record.balance}
                              </span>
                            </td>
                            <td>
                              {record.mode ? (
                                <span className={`badge ${
                                  record.mode === 'cash' ? 'bg-success' : 
                                  record.mode === 'card' ? 'bg-primary' : 'bg-info'
                                }`}>
                                  {record.mode.toUpperCase()}
                                </span>
                              ) : (
                                <span className="badge bg-secondary">PENDING</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="table-warning" style={{backgroundColor: '#fff3cd'}}>
                          <td colSpan="6" className="text-end fw-bold fs-5" style={{fontWeight: '700'}}>
                            Total Pending Amount:
                          </td>
                          <td className="fw-bold" style={{backgroundColor: '#dc3545', fontWeight: '700'}}>
                            <span className="badge bg-danger fs-5" style={{fontSize: '1.1rem !important', fontWeight: '700'}}>
                              ₹{sortedPendingRecords.reduce((total, record) => total + (parseFloat(record.balance) || 0), 0).toLocaleString()}
                            </span>
                          </td>
                          <td colSpan="1" style={{backgroundColor: '#fff3cd'}}></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </CardComponent>
            </div>
          </div>
        )}

        {viewMode === 'tablewise' && (
          <div className="row">
            <div className="col-12">
              <CardComponent 
                title={`Table Wise Pending Summary (${tableWiseData.length} tables with pending amounts)`}
                headerColor="info" 
                pull="left"
              >
                {/* Export Buttons for Tablewise */}
                {tableWiseData.length > 0 && (
                  <div className="d-flex justify-content-end gap-2 mb-3">
                    <button 
                      type="button" 
                      className="btn btn-success btn-sm"
                      onClick={exportTablewiseToPDF}
                    >
                      <FaFilePdf className="me-1" />
                      Export PDF
                    </button>
                    <button 
                      type="button" 
                      className="btn btn-info btn-sm"
                      onClick={exportTablewiseToExcel}
                    >
                      <FaFileExcel className="me-1" />
                      Export Excel
                    </button>
                    <button 
                      type="button" 
                      className="btn btn-warning btn-sm"
                      onClick={printTablewiseThermal}
                    >
                      <FaPrint className="me-1" />
                      Thermal Print
                    </button>
                  </div>
                )}
                {loadingTableWise ? (
                  <div className="text-center py-3">
                    <FaSpinner className="fa-spin me-2" />
                    Loading table wise data...
                  </div>
                ) : tableWiseData.length === 0 ? (
                  <div className="text-center text-muted py-4">
                    <FaHome size={32} className="mb-3 text-success" />
                    <h6>No Pending Table Amounts</h6>
                    <p>All tables have been fully settled!</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-striped table-hover">
                      <thead className="table-info">
                        <tr>
                          <th onClick={() => handleTablewiseSort('tableNumber')} style={{ cursor: 'pointer', userSelect: 'none', color: 'white' }}>
                            Table No. {getSortIcon('tableNumber', tablewiseSortConfig)}
                          </th>
                          <th onClick={() => handleTablewiseSort('recordCount')} style={{ cursor: 'pointer', userSelect: 'none', color: 'white' }}>
                            Orders Count {getSortIcon('recordCount', tablewiseSortConfig)}
                          </th>
                          <th onClick={() => handleTablewiseSort('totalNetAmount')} style={{ cursor: 'pointer', userSelect: 'none', color: 'white' }}>
                            Total Net Amount {getSortIcon('totalNetAmount', tablewiseSortConfig)}
                          </th>
                          <th onClick={() => handleTablewiseSort('totalPaidAmount')} style={{ cursor: 'pointer', userSelect: 'none', color: 'white' }}>
                            Total Paid {getSortIcon('totalPaidAmount', tablewiseSortConfig)}
                          </th>
                          <th onClick={() => handleTablewiseSort('totalBalance')} style={{ cursor: 'pointer', userSelect: 'none', color: 'white' }}>
                            Total Balance {getSortIcon('totalBalance', tablewiseSortConfig)}
                          </th>
                          <th style={{ color: 'white' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedTableWiseData.map((tableData, index) => (
                          <tr key={index}>
                            <td>
                              <strong className="text-primary">Table {tableData.tableNumber}</strong>
                            </td>
                            <td>
                              <span className="badge bg-secondary">{tableData.recordCount} orders</span>
                            </td>
                            <td>₹{tableData.totalNetAmount.toLocaleString()}</td>
                            <td>₹{tableData.totalPaidAmount.toLocaleString()}</td>
                            <td>
                              <span className="badge bg-warning text-dark fs-6">
                                ₹{tableData.totalBalance.toLocaleString()}
                              </span>
                            </td>
                            <td>
                              <button
                                className="btn btn-xs btn-info"
                                style={{backgroundColor: '#17a2b8', color: 'white', fontWeight: '600'}}
                                onClick={() => {
                                  setSelectedTableData(tableData);
                                  setShowTableModal(true);
                                }}
                              >
                                View Details
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="table-primary" style={{backgroundColor: '#cff4fc'}}>
                          <td className="fw-bold fs-5" style={{fontWeight: '700', backgroundColor: '#0d6efd', color: 'white'}}>
                            Total:
                          </td>
                          <td className="fw-bold fs-5" style={{fontWeight: '700', backgroundColor: '#198754', color: 'white'}}>
                            {sortedTableWiseData.reduce((total, table) => total + table.recordCount, 0)} orders
                          </td>
                          <td className="fw-bold fs-5" style={{fontWeight: '700', backgroundColor: '#198754', color: 'white'}}>
                            ₹{sortedTableWiseData.reduce((total, table) => total + table.totalNetAmount, 0).toLocaleString()}
                          </td>
                          <td className="fw-bold fs-5" style={{fontWeight: '700', backgroundColor: '#0dcaf0', color: 'black'}}>
                            ₹{sortedTableWiseData.reduce((total, table) => total + table.totalPaidAmount, 0).toLocaleString()}
                          </td>
                          <td className="fw-bold fs-5" style={{fontWeight: '700', backgroundColor: '#dc3545', color: 'white'}}>
                            ₹{sortedTableWiseData.reduce((total, table) => total + table.totalBalance, 0).toLocaleString()}
                          </td>
                          <td style={{backgroundColor: '#cff4fc'}}></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </CardComponent>
            </div>
          </div>
        )}
      </div>

      {/* Table Details Modal */}
      {selectedTableData && (
        <div className={`modal fade table-modal ${showTableModal ? 'show d-block' : ''}`} style={{backgroundColor: showTableModal ? 'rgba(0,0,0,0.5)' : '', display: showTableModal ? 'flex' : 'none', alignItems: 'center', justifyContent: 'center'}} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered" style={{
            maxWidth: '90vw', 
            width: '90vw',
            margin: 0
          }}>
            <div className="modal-content" style={{
              maxHeight: '85vh', 
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}>
              <div className="modal-header bg-info text-white">
                <h5 className="modal-title">
                  <FaHome className="me-2" />
                  Table {selectedTableData.tableNumber} - Pending Bills Details
                </h5>
                <div className="d-flex gap-2">
                  
                  <button 
                    type="button" 
                    className="btn btn-sm btn-outline-light"
                    onClick={printThermal}
                    title="Print Thermal Receipt"
                  >
                    <FaPrint />
                  </button>
                  <button 
                    type="button" 
                    className="btn-close btn-close-white" 
                    onClick={() => {
                      setShowTableModal(false);
                      setSelectedTableData(null);
                    }}
                  ></button>
                </div>
              </div>
              <div className="modal-body" style={{
                overflowY: 'auto', 
                flex: 1, 
                padding: '1.5rem',
                maxHeight: 'calc(85vh - 120px)'
              }}>
                {/* Table Summary */}
                <div className="row mb-4">
                  <div className="col-md-3">
                    <div className="card text-center bg-light">
                      <div className="card-body py-3">
                        <h6 className="card-title text-muted mb-1">Total Orders</h6>
                        <h4 className="text-primary mb-0">{selectedTableData.recordCount}</h4>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="card text-center bg-light">
                      <div className="card-body py-3">
                        <h6 className="card-title text-muted mb-1">Net Amount</h6>
                        <h4 className="text-success mb-0">₹{selectedTableData.totalNetAmount.toLocaleString()}</h4>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="card text-center bg-light">
                      <div className="card-body py-3">
                        <h6 className="card-title text-muted mb-1">Paid Amount</h6>
                        <h4 className="text-info mb-0">₹{selectedTableData.totalPaidAmount.toLocaleString()}</h4>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="card text-center bg-light">
                      <div className="card-body py-3">
                        <h6 className="card-title text-muted mb-1">Balance Due</h6>
                        <h4 className="text-danger mb-0">₹{selectedTableData.totalBalance.toLocaleString()}</h4>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Bills Details Table */}
                <div className="table-responsive">
                  <table className="table table-hover table-striped">
                    <thead className="table-dark">
                      <tr>
                        <th>Bill No.</th>
                        <th>Date & Time</th>
                        <th>Net Amount</th>
                        <th>Paid Amount</th>
                        <th>Balance</th>
                        <th>Payment Mode</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedTableData.records.map((record, index) => (
                        <tr key={record.id || index}>
                          <td>
                            <strong className="text-primary">{record.barbill}</strong>
                          </td>
                          <td>
                            <div>
                              <small className="d-block">{record.dte}</small>
                              <small className="text-muted">{record.tme ? record.tme.split('.')[0] : ''}</small>
                            </div>
                          </td>
                          <td>₹{parseFloat(record.netamount).toLocaleString()}</td>
                          <td>₹{parseFloat(record.paidamount || 0).toLocaleString()}</td>
                          <td>
                            <span className="badge bg-warning text-dark">
                              ₹{parseFloat(record.balance).toLocaleString()}
                            </span>
                          </td>
                          <td>
                            {record.mode ? (
                              <span className={`badge ${
                                record.mode === 'cash' ? 'bg-success' : 
                                record.mode === 'card' ? 'bg-primary' : 'bg-info'
                              }`}>
                                {record.mode.toUpperCase()}
                              </span>
                            ) : (
                              <span className="badge bg-secondary">PENDING</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="modal-footer">
                <div className="d-flex gap-2 me-auto">
                  
                  <button 
                    type="button" 
                    className="btn btn-warning btn-sm"
                    onClick={printThermal}
                  >
                    <FaPrint className="me-1" />
                    Thermal Print
                  </button>
                </div>
                <div className="d-flex gap-2">
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={() => {
                      setShowTableModal(false);
                      setSelectedTableData(null);
                    }}
                  >
                    Close
                  </button>
                 
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Floating Navigation Modal */}
      <FloatingNavModal currentPage="barcode" />
      
      {/* Toast Container for Notifications */}
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
      />
    </div>
  );
};

export default BarcodePayment;